// BAR 0 — THE APP BAR (owner directive, 2026-07-21, item 4/5: "build an
// always visible small ribbon above the current 2 with some controls for
// saving designs etc, undo/redo, printing, size controls and the background
// … the poster needs a properties section … The current settings and profile
// icon and AI credits can live in this ribbon too").
//
// A compact (~38px) always-visible strip ABOVE Row 1/Row 2, part of the
// ribbon stack (rendered by RibbonMount, above the morphing rows). Left →
// right:
//   · SAVE — the one Save home (the floating bottom-right button is gone).
//     Dirty dot rides editor.isDirty() (history position vs last-saved
//     marker); the click runs the EXISTING save flow via the host: first
//     save opens the name modal, later saves quiet-save under the same name.
//   · UNDO/REDO — the history cluster, MOVED here from the ribbon's left
//     rail (Ribbon.jsx no longer renders .rbn-history). Same markup contract
//     (.rbn-history-btn[data-verb], Ctrl+Z/Ctrl+Y tooltips), same
//     'history.undo'/'history.redo' handlers, same undoAvailable/
//     redoAvailable mirrors — only the address changed (.rbn-appbar).
//   · PRINT — a flyout (the app's only print path is export→Print Preview,
//     so the flyout says exactly that): poster size summary + the shared
//     print-sharpness (DPI) selector + one honest action that runs the
//     existing exportWorkspaceBlob pipeline and hands off to the print
//     screen (host onPrint — the SaveDesignModal's handleOpenInPrint recipe).
//   · POSTER — the poster properties flyout: size in INCHES (72 canvas units
//     = 1 in, the print300 contract; Apply = ONE changeSize = ONE undo
//     entry), export DPI (150/300/600, stored on the workspace data bag —
//     rides the design), the poster-light sun dial and the Background paint
//     chip — the last two MOVED from pageRow1 with their handlers and
//     mirrors intact ('page.setPosterLight' defer + history.commitNow;
//     'page.setBackground'/'page.setBackgroundRecipe' — the Background chip
//     opens the house paint MODAL (owner 2026-07-21) and closes this Poster
//     flyout; data-paint-open="pageBg" kept for the probes).
//   · right side: AI credits pill + settings gear + profile — MOVED from the
//     app's old floating header (WalletDisplay + TopRightNavbar), which
//     App.jsx now hides on the design page so Bar 0 is THE top chrome here.
//
// Everything here is chrome — no canvas data shapes change except dpi/size,
// which ride the EXISTING design meta (workspace data bag / changeSize).
import React, { useState } from 'react';
import RibbonFlyout from './core/RibbonFlyout';
import { PaintChip } from '../primitives/PaintPicker';
import { openPaintModal } from '../paintModalController';
import {
  IconSave, IconPrinter, IconPoster, IconUndo, IconRedo,
  IconWallet, IconGear, IconUser,
} from '../toolbarIcons';
import { useAuth } from '../../../../contexts/AuthContext';
import { Icon } from '../../../../ui/index.jsx';
import { initialsFor, useSession } from '../../../../app/session.jsx';
import { useWallet } from '../../../../contexts/WalletContext';
// RS lane: machine-local view prefs (rulers on/off + ruler units) — shared
// store with the canvas viewport chrome; localStorage pseCanvasPrefs.v1.
// NOTE: the Poster flyout's size fields stay INCHES regardless (print
// contract) — these units drive the RULERS.
import { useCanvasPrefs, setCanvasPrefs } from '../viewport/canvasPrefs';
import CreditPurchaseModal from '../../../wallet/CreditPurchaseModal';
import FeatureGate from '../../../common/FeatureGate';

const DPI_OPTIONS = [
  { value: 150, label: 'Standard (150 DPI)' },
  { value: 300, label: 'Best (300 DPI)' },
  { value: 600, label: 'Maximum (600 DPI)' },
];

const dpiLabel = (v) => (DPI_OPTIONS.find((o) => o.value === v) || DPI_OPTIONS[1]).label;

/** Shared print-sharpness selector — the Poster flyout's canonical control,
 *  summarized again inside Print. One handler ('page.setExportDPI'), one
 *  mirror (state.exportDPI), so the two faces can never disagree. */
function DpiSelect({ state, handlers, where }) {
  return (
    <label className="rbn-appbar-field">
      <span className="rbn-appbar-field-label">Print sharpness</span>
      <select
        className="rbn-appbar-select"
        data-appbar-dpi={where}
        value={String(state.exportDPI ?? 300)}
        onChange={(e) => handlers['page.setExportDPI']?.(parseInt(e.target.value, 10))}
      >
        {DPI_OPTIONS.map((o) => (
          <option key={o.value} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Poster properties panel body. Mounted fresh each time the flyout opens,
 *  so the size drafts re-seed from the live mirrors. Apply commits ONE
 *  changeSize (one undo entry); Enter in either field applies too. */
function PosterPanel({ state, handlers, close }) {
  const [wIn, setWIn] = useState(state.posterWidthIn != null ? String(state.posterWidthIn) : '');
  const [hIn, setHIn] = useState(state.posterHeightIn != null ? String(state.posterHeightIn) : '');
  const w = parseFloat(wIn);
  const h = parseFloat(hIn);
  const valid = Number.isFinite(w) && Number.isFinite(h) && w >= 1 && h >= 1 && w <= 200 && h <= 200;
  const changed = valid
    && (w !== state.posterWidthIn || h !== state.posterHeightIn);
  const applySize = () => {
    if (changed) handlers['page.setSizeInches']?.(w, h);
  };
  const onKey = (e) => { if (e.key === 'Enter') applySize(); };
  const angle = Math.round(Number.isFinite(state.posterLightAngle) ? state.posterLightAngle : 135);
  // The background chip's truthful face + the modal's seed value: the live
  // recipe when one is set, else the flat fill.
  const bgValue = state.workspacePaint && typeof state.workspacePaint === 'object'
    ? state.workspacePaint
    : (typeof state.workspaceFill === 'string' ? state.workspaceFill : '#ffffff');
  return (
    <>
      <div className="rbn-menu-title">Poster properties</div>

      <div className="rbn-appbar-field-label">Poster size (inches)</div>
      <div className="rbn-appbar-sizerow">
        <input
          type="number"
          className="rbn-appbar-num"
          data-size-field="w"
          aria-label="Poster width in inches"
          min={1}
          max={200}
          step={0.5}
          value={wIn}
          onChange={(e) => setWIn(e.target.value)}
          onKeyDown={onKey}
        />
        <span className="rbn-appbar-x">×</span>
        <input
          type="number"
          className="rbn-appbar-num"
          data-size-field="h"
          aria-label="Poster height in inches"
          min={1}
          max={200}
          step={0.5}
          value={hIn}
          onChange={(e) => setHIn(e.target.value)}
          onKeyDown={onKey}
        />
        <button
          type="button"
          className="rbn-appbar-apply"
          data-appbar-action="apply-size"
          disabled={!changed}
          title={valid ? 'Resize the poster (everything stays where it is)' : 'Enter a width and height from 1 to 200 inches'}
          onClick={applySize}
        >
          Apply
        </button>
      </div>

      <DpiSelect state={state} handlers={handlers} where="poster" />

      <div className="rbn-appbar-field" data-control-id="posterLight">
        <span className="rbn-appbar-field-label">
          Poster light — Sun {angle}°
          {state.posterLightOn && <span className="rbn-dot" title="Something on this poster follows the sun" />}
        </span>
        <input
          type="range"
          min={0}
          max={360}
          step={5}
          value={angle}
          aria-label="Poster light direction"
          onChange={(e) => handlers['page.setPosterLight']?.(parseInt(e.target.value, 10))}
          onPointerUp={() => handlers['history.commitNow']?.()}
          onBlur={() => handlers['history.commitNow']?.()}
        />
      </div>

      <div className="rbn-appbar-field">
        <span className="rbn-appbar-field-label">Background</span>
        {/* Owner 2026-07-21: the chip opens the ONE house paint modal (title
            "Poster background") and CLOSES this Poster flyout — one floating
            surface. color + gradient + texture: solids ride page.setBackground,
            gradient AND texture ride page.setBackgroundRecipe (the BG lane is
            landing setBackgroundRecipe texture support in parallel; a texture
            pick is an object → routes there, as if present). Gradient stays
            linear-only (the page engine's truth). */}
        <span className="rbn-appbar-field-chip" data-paint-open="pageBg">
          <PaintChip
            value={bgValue}
            className="rbn-swatch-face rbn-appbar-bgchip"
            title="Poster background"
            ariaHaspopup="dialog"
            onClick={() => {
              openPaintModal({
                fieldId: 'pageBg',
                title: 'Poster background',
                value: bgValue,
                allow: ['solid', 'gradient', 'texture'],
                linearOnly: true,
                palette: Array.isArray(state.backgroundPresets) ? state.backgroundPresets : undefined,
                density: 'compact',
                onChange: (next, { live } = {}) => {
                  if (typeof next === 'string') handlers['page.setBackground']?.(next, live ? 'soon' : 'now');
                  else handlers['page.setBackgroundRecipe']?.(next, live ? 'defer' : 'now');
                },
                onCommit: () => handlers['history.commitNow']?.(),
              });
              close?.();
            }}
          />
        </span>
      </div>

      <button
        type="button"
        className="rbn-menu-footer"
        data-appbar-action="size-panel"
        onClick={() => { handlers['page.openSizePanel']?.(); close?.(); }}
      >
        All size presets…
      </button>
    </>
  );
}

export default function AppBar({ editor, state = {}, handlers = {}, app = {} }) {
  const { isAuthenticated } = useAuth();
  const { balance, balanceChanged } = useWallet();
  const { session } = useSession();
  const initials = initialsFor(session);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const canvasPrefs = useCanvasPrefs(); // rulers/units mirror (gear flyout)

  const dirty = !!app.isDirty;
  const saveTitle = app.saveBusy
    ? 'Saving…'
    : dirty
      ? 'Save your poster (you have unsaved changes)'
      : 'Saved — everything is in your library';

  return (
    <div className="rbn-appbar" data-appbar="true" role="toolbar" aria-label="Poster actions">
      {/* ---- Save (the one home; first save names it, later saves are quiet) ---- */}
      <button
        type="button"
        className={`rbn-appbar-btn rbn-appbar-save${dirty ? ' is-dirty' : ''}`}
        data-appbar-btn="save"
        title={saveTitle}
        aria-label="Save design"
        disabled={!!app.saveBusy || !editor}
        onClick={() => app.onSave?.()}
      >
        <IconSave size={15} />
        <span className="rbn-appbar-btn-label">{app.saveBusy ? 'Saving…' : 'Save'}</span>
        {dirty && !app.saveBusy && <span className="rbn-appbar-dirty" aria-hidden="true" />}
      </button>

      {/* ---- the history cluster (same contract as the retired left rail) ---- */}
      <div className="rbn-history" role="group" aria-label="History">
        <button
          type="button"
          className="rbn-history-btn"
          data-verb="undo"
          title="Undo (Ctrl+Z)"
          aria-label="Undo (Ctrl+Z)"
          disabled={!state.undoAvailable}
          onClick={() => handlers['history.undo']?.()}
        >
          <IconUndo size={15} />
        </button>
        <button
          type="button"
          className="rbn-history-btn"
          data-verb="redo"
          title="Redo (Ctrl+Y)"
          aria-label="Redo (Ctrl+Y)"
          disabled={!state.redoAvailable}
          onClick={() => handlers['history.redo']?.()}
        >
          <IconRedo size={15} />
        </button>
      </div>

      <span className="rbn-appbar-rule" aria-hidden="true" />

      {/* ---- Poster properties (size / sharpness / light / background) ---- */}
      <RibbonFlyout
        flyId="appbarPoster"
        label="Poster"
        faceClassName="rbn-appbar-fly"
        active={!!state.posterLightOn}
        face={(
          <>
            <span className="bv-appbar-title" title={app.designName || 'Untitled design'}>{app.designName || 'Untitled design'}</span>
            <span className="bv-appbar-tag">
              <IconPoster size={13} />
              {state.posterSizeLabel ? `Poster · ${state.posterSizeLabel}` : 'Poster'}
            </span>
          </>
        )}
        panelClassName="rbn-appbar-poster-panel"
        panel={({ close }) => (
          <PosterPanel state={state} handlers={handlers} close={close} />
        )}
      />

      <span className="rbn-appbar-spacer" aria-hidden="true" />

      {/* ---- account cluster (moved from the old floating header) ---- */}
      {isAuthenticated && (
        <button
          type="button"
          className={`rbn-appbar-pill${balanceChanged ? ' is-glow' : ''}`}
          data-appbar-btn="credits"
          title="AI credits — click to get more"
          aria-label="AI credits"
          onClick={() => setCreditsOpen(true)}
        >
          <Icon name="sparkle" size={18} />
          <span className="rbn-appbar-balance">{balance == null ? '…' : `${balance} ${balance === 1 ? 'credit' : 'credits'}`}</span>
        </button>
      )}
      <RibbonFlyout
        flyId="appbarSettings"
        label="Settings"
        faceClassName="rbn-appbar-iconbtn"
        face={<IconGear size={15} />}
        panel={({ close }) => (
          <>
            <FeatureGate component="print_preview" feature="print_history" label="Print History" tooltipPosition="left">
              <button
                type="button"
                className="rbn-menu-action"
                data-appbar-action="print-jobs"
                onClick={() => { close(); app.onOpenPrintJobs?.(); }}
              >
                Print jobs
              </button>
            </FeatureGate>
            <button
              type="button"
              className="rbn-menu-action"
              data-appbar-action="canvas-settings"
              onClick={() => { handlers['page.openSizePanel']?.(); close(); }}
            >
              Canvas settings
            </button>
            {/* RS lane — view prefs live HERE (the honest home: "Canvas
                settings" above is a stub that routes to the size panel).
                Educator words; persisted per machine, never in the design. */}
            <div className="rbn-fly-divider" aria-hidden="true" />
            <label className="rbn-fly-check-row" data-appbar-pref="rulers">
              <input
                type="checkbox"
                checked={!!canvasPrefs.rulers}
                onChange={(e) => setCanvasPrefs({ rulers: e.target.checked })}
              />
              <span>Show rulers</span>
            </label>
            <label className="rbn-appbar-field" data-appbar-pref="units">
              <span className="rbn-appbar-field-label">Units</span>
              <select
                className="rbn-appbar-select"
                aria-label="Ruler units"
                value={canvasPrefs.units}
                onChange={(e) => setCanvasPrefs({ units: e.target.value })}
              >
                <option value="in">Inches</option>
                <option value="cm">Centimeters</option>
                <option value="px">Pixels</option>
              </select>
            </label>
          </>
        )}
      />
      <button
        type="button"
        className="cce-tb-btn rbn-appbar-iconbtn"
        data-appbar-btn="profile"
        title="Your account"
        aria-label="Your account"
        onClick={() => window.dispatchEvent(new Event('pse-open-account'))}
      >
        <span className="bv-appbar-avatar">{initials}</span>
      </button>

      {/* ---- Print (honest: export at the chosen sharpness → Print Preview) ---- */}
      <RibbonFlyout
        flyId="appbarPrint"
        label="Print"
        faceClassName="rbn-appbar-fly bv-appbar-print"
        face={(
          <>
            <Icon name="print" size={20} />
            <span className="rbn-appbar-btn-label">Print</span>
          </>
        )}
        panel={({ close }) => (
          <>
            <div className="rbn-menu-title">Print this poster</div>
            <div className="rbn-appbar-summary">
              {state.posterSizeLabel ? `Poster size: ${state.posterSizeLabel}` : 'Poster size: —'}
              <br />
              Sharpness: {dpiLabel(state.exportDPI ?? 300)}
            </div>
            <DpiSelect state={state} handlers={handlers} where="print" />
            <button
              type="button"
              className="rbn-appbar-primary"
              data-appbar-action="print"
              disabled={!!app.printBusy || !editor}
              title="Makes a print-ready copy and opens it on the Print screen"
              onClick={() => { close(); app.onPrint?.(); }}
            >
              {app.printBusy ? 'Preparing…' : 'Print'}
            </button>
            <div className="rbn-appbar-hint">
              Makes a print-ready copy and opens it on the Print screen.
            </div>
          </>
        )}
      />


      <CreditPurchaseModal isOpen={creditsOpen} onClose={() => setCreditsOpen(false)} />
    </div>
  );
}
